/**
 * Readiness check rendering shared by the site's "Ready for JBS" panel and
 * the JBS install dialog's `site_not_ready` refusal (#796 / #804).
 *
 * Each check id is a stable reason code; the install docs (#839) have one
 * section per id, anchored by the id itself, so every row links there.
 */
import { Check, X, HelpCircle, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { readinessDocsUrl } from '@/lib/readiness';
import type { ReadinessCheck, ReadinessCheckStatus } from '@/types';

function StatusIcon({ status }: { status: ReadinessCheckStatus | string }) {
  if (status === 'pass') return <Check size={14} className="text-status-healthy shrink-0" aria-label="pass" />;
  if (status === 'fail') return <X size={14} className="text-status-error shrink-0" aria-label="fail" />;
  return <HelpCircle size={14} className="text-status-warning shrink-0" aria-label="unknown" />;
}

type CheckLike = Pick<ReadinessCheck, 'id' | 'status' | 'summary' | 'remediation'> & Partial<Pick<ReadinessCheck, 'detail' | 'blocking'>>;

export function ReadinessCheckRow({ check }: { check: CheckLike }) {
  const passing = check.status === 'pass';
  const nonBlocking = check.status === 'unknown' && check.blocking === false;
  return (
    <li className="py-2 first:pt-0 last:pb-0" data-testid={`readiness-check-${check.id}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5"><StatusIcon status={check.status} /></span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <a
              href={readinessDocsUrl(check.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[11px] text-gold hover:text-gold-light transition-colors"
              title="What this check means and how to fix it"
            >
              {check.id}
              <ExternalLink size={10} />
            </a>
            {nonBlocking && <span className="text-[10px] text-silver-muted">(not blocking)</span>}
          </div>
          <p className={cn('text-xs', passing ? 'text-silver-muted' : 'text-silver-bright')}>{check.summary}</p>
          {!passing && check.remediation && (
            <p className="text-xs text-status-warning/90">{check.remediation}</p>
          )}
          {!passing && check.detail && (
            <details className="text-[11px] text-silver-muted">
              <summary className="cursor-pointer select-none hover:text-silver-bright">Details</summary>
              <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] bg-surface-deep rounded p-2">{check.detail}</pre>
            </details>
          )}
        </div>
      </div>
    </li>
  );
}

/** Failing checks first (then unknown, then passing), keeping server order within each group. */
export function sortChecks<T extends { status: string }>(checks: T[]): T[] {
  const rank = (s: string) => (s === 'fail' ? 0 : s === 'pass' ? 2 : 1);
  return checks
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c.status) - rank(b.c.status) || a.i - b.i)
    .map(({ c }) => c);
}

export function ReadinessCheckList({ checks }: { checks: CheckLike[] }) {
  return (
    <ul className="divide-y divide-surface-elevated">
      {sortChecks(checks).map((c) => (
        <ReadinessCheckRow key={c.id} check={c} />
      ))}
    </ul>
  );
}
